import "../sellscreendialogs.scss";
import * as React from "react";
import { connect, type ConnectedProps } from "react-redux";
import { type ThunkDispatch } from "redux-thunk";
import { hideDialog } from "../../../../Actions/Dialogs/DialogActions";
import { addPaymentToSale, type SaleAddPaymentOptions } from "../../../../Actions/SellScreen/SellScreenPaymentActions";
import Button from "../../../../Common/Buttons/Button";
import NoTranslate from "../../../../Common/NoTranslate";
import PopupManager from "../../../../Common/Popups/PopupManager";
import TouchNumbers from "../../../../Common/TouchNumbers/TouchNumbers";
import { type LocalDatabasePaymentMethod } from "../../../../Data/_Bases/BasePaymentMethodRepository";
import { determineDataSource } from "../../../../Data/DataStores/DataSource";
import { currency, roundTo } from "../../../../Utilities/Generic/Numbers";
import { CancellablePromise } from "../../../../Utilities/Generic/Promises";
import { getOption } from "../../../../Utilities/Platform/Options/Options";
import { type LocalPaymentMethodType } from "../../../Settings/PaymentMethods/PaymentMethodTypes";

const connector = connect();

interface Props extends ConnectedProps<typeof connector> {
    dispatch: ThunkDispatch<any, any, any>;
    method: string;
    amount: number;
    options: SaleAddPaymentOptions;
}

type State = {
    value: string;
    loading: boolean;
    cashOutMethod: null | LocalPaymentMethodType;
};

export class RequestCashout extends React.Component<Props, State> {
    protected cashoutPromise: null | CancellablePromise<LocalDatabasePaymentMethod> = null;
    protected inputRef: React.RefObject<HTMLInputElement | null>;

    constructor(props: Props) {
        super(props);

        this.state = {
            value        : "",
            loading      : true,
            cashOutMethod: null,
        };

        this.inputRef = React.createRef();

        this.addPayment = this.addPayment.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.cancelRequest = this.cancelRequest.bind(this);
        this.handleConfirm = this.handleConfirm.bind(this);
    }

    public componentDidMount(): void {
        if(this.inputRef.current) {
            this.inputRef.current.focus();
        }

        this.loadMethod();
    }

    protected loadMethod(): void {
        this.setState({
            loading: true,
        });

        this.cashoutPromise = new CancellablePromise(
            determineDataSource()
                .then(async source => {
                    const method = await source.paymentMethods.get(this.props.method);

                    if(!method.cashout_method_id) {
                        throw new Error(
                            "No cash out payment method available for provided method " + this.props.method
                        );
                    }

                    return source.paymentMethods.get(method.cashout_method_id);
                })
        );

        this.cashoutPromise
            .promise
            .then(cashOutMethod => {
                this.setState({
                    loading: false,
                    cashOutMethod,
                });
            })
            .catch(err => {
                console.error("Error loading cash out method", err);
                PopupManager.error("Failed to load cash out method");
            });
    }

    protected handleChange(value: string): void {
        this.setState({
            value: value,
        });
    }

    protected handleConfirm(value: string): void {
        if(this.state.loading) {
            return;
        }

        const parsed = parseFloat(value);

        if(parsed > 999) {
            PopupManager.info("Please click \"Add\" to confirm the payment amount");

            return;
        }

        this.addPayment();
    }

    protected addPayment(): void {
        if(this.state.cashOutMethod?.use_rounding && this.state.value !== "") {
            const parsed = parseFloat(this.state.value);

            // Calculate rounding
            const roundToOption = getOption("round_to");

            if(roundToOption !== 0) {
                const rounded = roundTo(parsed, roundToOption);

                if(rounded !== parsed) {
                    PopupManager.warning("Invalid amount for the cash out payment method");

                    return;
                }
            }
        }

        this.props.dispatch(hideDialog());
        this.props.dispatch(addPaymentToSale(this.props.method, this.props.amount, {
            ...this.props.options,
            cashout: parseFloat(this.state.value),
        }));
    }

    protected cancelRequest(): void {
        this.props.dispatch(hideDialog());
    }

    public render(): React.ReactNode {
        let original = 0;

        if(this.props.amount) {
            original = this.props.amount;
        }

        if(this.props.options.rounding && this.props.options.rounding !== "auto") {
            original += this.props.options.rounding;
        }

        let cashout = 0;

        if(this.state.value) {
            cashout = parseFloat(this.state.value);
        }

        return (
            <div className="request-cashout">
                <p>Please enter the cash out amount</p>
                <div className="totals">
                    <span>Original: <NoTranslate>{currency(original)}</NoTranslate></span>
                    <span>Total: <NoTranslate>{currency(original + cashout)}</NoTranslate></span>
                </div>
                <TouchNumbers
                    static
                    hideConfirm
                    bounds={{
                        min: 0,
                        max: Infinity,
                    }}
                    input="currency"
                    value={this.state.value}
                    onChange={this.handleChange}
                    onConfirm={this.handleConfirm}
                />
                <div className="buttons">
                    <Button
                        type="button"
                        classes={[ "cancel-button", "button-cancel" ]}
                        onClick={this.cancelRequest}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        classes={[ "add-button", "button-primary" ]}
                        onClick={this.addPayment}
                        disabled={this.state.loading && !!this.state.value}
                    >
                        {(!cashout ? "Skip" : "Add")}
                    </Button>
                </div>
            </div>
        );
    }
}

export default connector(RequestCashout);
